import { useId, useState, type FC, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Icon } from '@/components/icons';
import { COMMON_CLASSES } from '@/styles/classes';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
}

export const Input: FC<InputProps> = ({
  label,
  error,
  helperText,
  className = '',
  containerClassName,
  id,
  type,
  ...props
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id || generatedId;
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label ? (
        <label htmlFor={inputId} className={COMMON_CLASSES.label}>
          {label}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={inputId}
          type={inputType}
          className={clsx(
            COMMON_CLASSES.input,
            error && COMMON_CLASSES.inputError,
            isPasswordField && 'pr-12',
            className
          )}
          {...props}
        />
        {isPasswordField ? (
          <button
            type="button"
            onClick={() => setShowPassword(current => !current)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-unilink-text-secondary"
            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            tabIndex={-1}
          >
            {showPassword ? (
              <Icon icon="eye" className="size-6" />
            ) : (
              <Icon icon="eye-slash" className="size-6" />
            )}
          </button>
        ) : null}
      </div>
      {error ? <p className={`mt-1 ${COMMON_CLASSES.textError}`}>{error}</p> : null}
      {helperText && !error ? (
        <p className={`mt-1 ${COMMON_CLASSES.textHelper}`}>{helperText}</p>
      ) : null}
    </div>
  );
};
